import React, { useCallback, useEffect, useState } from "react";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CBadge,
  CSpinner,
} from "@coreui/react-pro";
import { CChart } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import {
  cilCloudDownload,
  cilMoney,
  cilCart,
  cilBell,
  cilChartLine,
  cilArrowRight,
} from "@coreui/icons";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../hooks/useTheme";
import { useStore } from "../../contexts/StoreContext";
import * as DomicilappService from "../../services/DomicilappService";
import { DashboardRange, StoreDashboard } from "../../types/dashboard";
import { DeliveryTypeBadge, StatusBadge, formatCurrency, timeAgo } from "../orders/orderUtils";

const RANGE_LABELS: Record<DashboardRange, string> = { daily: "Hoy", weekly: "7 días", monthly: "30 días", annually: "12 meses" };
const PREVIOUS_LABELS: Record<DashboardRange, string> = { daily: "ayer", weekly: "7 días anteriores", monthly: "30 días anteriores", annually: "año anterior" };

const MODALITY_COLORS = { delivery: "#3B82F6", pickup: "#10B981", scheduled: "#F59E0B" };

// Variación porcentual frente al periodo anterior (null si no hay base para comparar)
const getChange = (current: number, previous: number) => (previous > 0 ? Math.round(((current - previous) / previous) * 100) : null);

const ChangeLabel: React.FC<{ change: number | null; range: DashboardRange }> = ({ change, range }) => {
  if (change === null) return <small className="text-body-secondary">Sin datos de {PREVIOUS_LABELS[range]}</small>;
  const positive = change >= 0;
  return (
    <small className={positive ? "text-success" : "text-danger"}>
      {positive ? "▲" : "▼"} {Math.abs(change)}% <span className="text-body-secondary">vs {PREVIOUS_LABELS[range]}</span>
    </small>
  );
};

const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  footer?: React.ReactNode;
  icon: string[];
  color: string;
  onClick?: () => void;
}> = ({ label, value, footer, icon, color, onClick }) => (
  <CCard
    className="border-0 shadow-sm rounded-3 bg-body-tertiary h-100"
    onClick={onClick}
    style={onClick ? { cursor: "pointer" } : undefined}
  >
    <CCardBody className="d-flex align-items-center justify-content-between p-3">
      <div>
        <span className="text-uppercase text-body-secondary fw-bold d-block" style={{ fontSize: "0.72rem", letterSpacing: "0.5px" }}>
          {label}
        </span>
        <h4 className="fw-bold mb-0 text-body mt-1">{value}</h4>
        {footer && <div className="mt-1">{footer}</div>}
      </div>
      <div
        className="rounded-circle d-flex align-items-center justify-content-center p-3"
        style={{ backgroundColor: color + "26", color }}
      >
        <CIcon icon={icon} size="xl" />
      </div>
    </CCardBody>
  </CCard>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { storeId, storeDetails } = useStore();
  const [range, setRange] = useState<DashboardRange>("daily");
  const [data, setData] = useState<StoreDashboard | null>(null);
  const [loading, setLoading] = useState(false);

  // Obtenemos el tema activo ('light' o 'dark')
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Colores dinámicos para ChartJS según el tema activo
  const chartTextColor = isDark ? "#CBD5E1" : "#475569";
  const chartGridColor = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)";

  const loadDashboard = useCallback(
    (silent = false) => {
      if (!storeId) return;
      if (!silent) setLoading(true);
      DomicilappService.getStoreDashboard(storeId, range)
        .then((response) => {
          if (response?.status === 200 && response.data) {
            setData(response.data);
          } else if (!silent) {
            setData(null);
          }
        })
        .catch((error) => {
          console.error("Error al obtener el dashboard:", error);
          if (!silent) setData(null);
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [storeId, range],
  );

  useEffect(() => {
    setData(null);
    loadDashboard();
  }, [loadDashboard]);

  // Las órdenes por atender cambian minuto a minuto
  useEffect(() => {
    const interval = setInterval(() => loadDashboard(true), 60000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  const downloadCSV = () => {
    if (!data) return;
    const headers = ["Periodo", "Órdenes", "Ventas", "Domicilio", "Recoger en tienda", "Programado"];
    const rows = data.traffic.map((t) => [t.field, t.orders, t.sales, t.delivery, t.pickup, t.scheduled]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" }));
    link.download = `reporte_domicilapp_${range}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const summary = data?.summary;
  const modalityTotal = summary ? summary.delivery_orders + summary.pickup_orders + summary.scheduled_orders : 0;
  const modalities = summary
    ? [
        { label: "Domicilio", value: summary.delivery_orders, color: MODALITY_COLORS.delivery },
        { label: "Recoger en tienda", value: summary.pickup_orders, color: MODALITY_COLORS.pickup },
        { label: "Programado", value: summary.scheduled_orders, color: MODALITY_COLORS.scheduled },
      ]
    : [];

  if (!storeId) {
    return (
      <CCard className="border-0 shadow-sm rounded-3 bg-body-tertiary">
        <CCardBody className="text-center py-5 text-body-secondary">Selecciona o crea una tienda para ver su dashboard.</CCardBody>
      </CCard>
    );
  }

  return (
    <div className="dashboard-container">
      {/* CABECERA */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <h3 className="fw-bold text-body mb-1">{storeDetails?.name ?? "Mi tienda"}</h3>
          <p className="text-body-secondary small mb-0">Resumen de ventas y operación de la tienda</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <CButtonGroup role="group" size="sm" className="bg-body-secondary p-1 rounded-3">
            {(Object.keys(RANGE_LABELS) as DashboardRange[]).map((option) => {
              const isActive = range === option;
              return (
                <CButton
                  key={option}
                  color={isActive ? "danger" : "transparent"}
                  className={`fw-bold border-0 px-3 py-1 ${isActive ? "text-white shadow-sm" : "text-body-secondary"}`}
                  style={isActive ? { backgroundColor: "#FE2445", borderRadius: "6px" } : {}}
                  onClick={() => setRange(option)}
                >
                  {RANGE_LABELS[option]}
                </CButton>
              );
            })}
          </CButtonGroup>
          <CButton
            color="light"
            size="sm"
            onClick={downloadCSV}
            disabled={!data}
            className="border text-body bg-body-secondary d-flex align-items-center gap-1 px-3 py-1 fw-medium"
          >
            <CIcon icon={cilCloudDownload} />
            <span>Exportar</span>
          </CButton>
        </div>
      </div>

      {loading && !data ? (
        <div className="text-center py-5">
          <CSpinner color="primary" size="sm" className="me-2" />
          <span className="text-body-secondary">Cargando indicadores de la tienda...</span>
        </div>
      ) : !data || !summary ? (
        <CCard className="border-0 shadow-sm rounded-3 bg-body-tertiary">
          <CCardBody className="text-center py-5 text-body-secondary">No fue posible cargar los indicadores de la tienda.</CCardBody>
        </CCard>
      ) : (
        <>
          {/* 1. KPIS */}
          <CRow className="g-3 mb-4">
            <CCol sm={6} lg={3}>
              <KpiCard
                label="Ventas"
                value={formatCurrency(summary.total_sales)}
                footer={<ChangeLabel change={getChange(summary.total_sales, summary.previous_total_sales)} range={range} />}
                icon={cilMoney}
                color="#FE2445"
              />
            </CCol>
            <CCol sm={6} lg={3}>
              <KpiCard
                label="Órdenes"
                value={
                  <>
                    {summary.total_orders} <small className="fs-6 fw-normal text-body-secondary">pedidos</small>
                  </>
                }
                footer={<ChangeLabel change={getChange(summary.total_orders, summary.previous_total_orders)} range={range} />}
                icon={cilCart}
                color="#3B82F6"
              />
            </CCol>
            <CCol sm={6} lg={3}>
              <KpiCard
                label="Ticket promedio"
                value={formatCurrency(summary.avg_ticket)}
                footer={
                  <small className="text-body-secondary">
                    {summary.peak_hour ? `Hora pico: ${summary.peak_hour}` : "Sin hora pico aún"}
                  </small>
                }
                icon={cilChartLine}
                color="#10B981"
              />
            </CCol>
            <CCol sm={6} lg={3}>
              <KpiCard
                label="Por atender ahora"
                value={
                  <>
                    {summary.active_orders} <small className="fs-6 fw-normal text-body-secondary">órdenes</small>
                  </>
                }
                footer={<small className="text-body-secondary">Ir a órdenes en curso →</small>}
                icon={cilBell}
                color="#F59E0B"
                onClick={() => navigate("/orders/active")}
              />
            </CCol>
          </CRow>

          {/* 2. GRÁFICO DE ÓRDENES POR PERIODO */}
          <CCard className="border-0 shadow-sm rounded-3 bg-body-tertiary mb-4">
            <CCardBody className="p-4">
              <div className="d-flex align-items-center mb-1">
                <h5 className="fw-bold text-body mb-0 me-2">Órdenes por modalidad</h5>
                {range === "daily" && (
                  <CBadge shape="rounded-pill" style={{ backgroundColor: "#FE2445", color: "#fff" }}>
                    EN VIVO
                  </CBadge>
                )}
              </div>
              <p className="text-body-secondary small mb-4">
                {summary.total_orders} órdenes · {summary.completed_orders} finalizadas · {RANGE_LABELS[range].toLowerCase()}
              </p>

              <div style={{ height: "320px", position: "relative" }}>
                <CChart
                  type="bar"
                  className="h-100"
                  data={{
                    labels: data.traffic.map((t) => t.field),
                    datasets: [
                      {
                        label: "Domicilio",
                        backgroundColor: MODALITY_COLORS.delivery,
                        borderRadius: 4,
                        maxBarThickness: 22,
                        data: data.traffic.map((t) => t.delivery),
                      },
                      {
                        label: "Recoger en tienda",
                        backgroundColor: MODALITY_COLORS.pickup,
                        borderRadius: 4,
                        maxBarThickness: 22,
                        data: data.traffic.map((t) => t.pickup),
                      },
                      {
                        label: "Programado",
                        backgroundColor: MODALITY_COLORS.scheduled,
                        borderRadius: 4,
                        maxBarThickness: 22,
                        data: data.traffic.map((t) => t.scheduled),
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "top",
                        align: "end",
                        labels: {
                          usePointStyle: true,
                          boxWidth: 8,
                          color: chartTextColor,
                          font: { size: 12, weight: "bold" },
                        },
                      },
                      tooltip: {
                        backgroundColor: isDark ? "#0F172A" : "#FFFFFF",
                        titleColor: isDark ? "#FFFFFF" : "#0F172A",
                        bodyColor: isDark ? "#FFFFFF" : "#0F172A",
                        borderColor: isDark ? "#334155" : "#E2E8F0",
                        borderWidth: 1,
                        padding: 12,
                        cornerRadius: 8,
                        callbacks: {
                          footer: (items: any[]) => {
                            const point = data.traffic[items[0]?.dataIndex];
                            return point ? `Ventas: ${formatCurrency(point.sales)}` : "";
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        stacked: true,
                        grid: { display: false },
                        ticks: { color: chartTextColor, font: { size: 12, weight: "500" } },
                      },
                      y: {
                        stacked: true,
                        beginAtZero: true,
                        grid: { color: chartGridColor },
                        ticks: { color: chartTextColor, precision: 0, font: { size: 12, weight: "500" } },
                      },
                    },
                  }}
                />
              </div>
            </CCardBody>
          </CCard>

          <CRow className="g-3 mb-4">
            {/* 3. ÓRDENES POR ATENDER */}
            <CCol lg={7}>
              <CCard className="border-0 shadow-sm rounded-3 bg-body-tertiary h-100">
                <CCardBody className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold text-body mb-0">Órdenes por atender</h5>
                    <CButton color="link" size="sm" className="text-decoration-none p-0 d-flex align-items-center gap-1" onClick={() => navigate("/orders/active")}>
                      Ver todas <CIcon icon={cilArrowRight} size="sm" />
                    </CButton>
                  </div>
                  {data.active_orders.length === 0 ? (
                    <p className="text-body-secondary small mb-0 py-4 text-center">No hay órdenes pendientes. ¡Todo al día!</p>
                  ) : (
                    data.active_orders.map((order, index) => (
                      <div
                        key={order.id}
                        className={`d-flex justify-content-between align-items-center py-2 ${index > 0 ? "border-top" : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate("/orders/active")}
                      >
                        <div>
                          <div className="fw-semibold text-body">
                            #{order.id} · {order.user_full_name}
                          </div>
                          <div className="d-flex align-items-center gap-2 mt-1">
                            <DeliveryTypeBadge typeId={order.delivery_type_id} name={order.delivery_type_name} />
                            <small className="text-body-secondary">{timeAgo(order.created_at)}</small>
                          </div>
                        </div>
                        <div className="text-end">
                          <StatusBadge name={order.status_name} color={order.status_color} />
                          <div className="fw-bold text-body small mt-1">{formatCurrency(order.total_price)}</div>
                        </div>
                      </div>
                    ))
                  )}
                </CCardBody>
              </CCard>
            </CCol>

            <CCol lg={5}>
              {/* 4. MÁS VENDIDOS */}
              <CCard className="border-0 shadow-sm rounded-3 bg-body-tertiary mb-3">
                <CCardBody className="p-4">
                  <h5 className="fw-bold text-body mb-3">Más vendidos</h5>
                  {data.top_products.length === 0 ? (
                    <p className="text-body-secondary small mb-0">Aún no hay ventas en este periodo.</p>
                  ) : (
                    data.top_products.map((product, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2 text-truncate">
                          <span className="fw-bold text-body-secondary" style={{ width: 18 }}>{index + 1}</span>
                          <span className="text-body text-truncate">{product.name}</span>
                          {product.is_combo === 1 && <CBadge color="secondary-subtle" className="text-secondary">Combo</CBadge>}
                        </div>
                        <div className="text-end text-nowrap ms-2">
                          <span className="fw-semibold text-body">{product.quantity} uds</span>
                          <small className="d-block text-body-secondary">{formatCurrency(product.total)}</small>
                        </div>
                      </div>
                    ))
                  )}
                </CCardBody>
              </CCard>

              {/* 5. MODALIDAD */}
              <CCard className="border-0 shadow-sm rounded-3 bg-body-tertiary">
                <CCardBody className="p-4">
                  <h5 className="fw-bold text-body mb-3">Modalidad de entrega</h5>
                  {modalities.map((m) => {
                    const percent = modalityTotal ? Math.round((m.value / modalityTotal) * 100) : 0;
                    return (
                      <div key={m.label} className="mb-3">
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-body">{m.label}</span>
                          <span className="text-body-secondary">
                            {m.value} · {percent}%
                          </span>
                        </div>
                        <div className="rounded-pill bg-body-secondary overflow-hidden" style={{ height: 6 }}>
                          <div className="h-100 rounded-pill" style={{ width: `${percent}%`, backgroundColor: m.color }} />
                        </div>
                      </div>
                    );
                  })}
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </>
      )}
    </div>
  );
};

export default Dashboard;
